import type { ColumnFiltersState } from '@tanstack/react-table'
import { z } from 'zod'

import type { ViewLevel } from '@/components/common/ViewLevelTabs'
import type { DataVaultFilterParams, DataVaultListParams } from '@/hooks/queries/dataVault'
import { primaryFilterSearchSchema, pruned, toApiParams } from '@/lib/filters'
import { fromFilterParams, toFilterParams } from '@/lib/tableFilters'

/**
 * The view state every Data Vault page reads from the URL.
 *
 * On top of the primary filters: the page and its size, the server sort,
 * the level (rows or a roll-up), the free-text search, the numeric column
 * filters in the backend's own `column:op:value` form, the hidden columns,
 * and the Orders trend controls. Every field is optional with a `.catch`,
 * defaults are applied at read time and stripped on the way out
 * (`withoutDataVaultDefaults`), so `/data-vault/orders` stays the clean URL
 * of the default view (rules.md E9).
 */

/** VentaHub's grid sizes; the API stops at 100. */
export const PAGE_SIZES = [10, 25, 50, 75, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 50

export const VIEW_MODES = ['rows', 'sku', 'brand', 'category', 'subcategory'] as const
export type ViewMode = (typeof VIEW_MODES)[number]
export const DEFAULT_VIEW: ViewMode = 'rows'

export const TREND_METRICS = ['units', 'revenue', 'orders'] as const
export type TrendMetric = (typeof TREND_METRICS)[number]
export const TREND_GRANULARITIES = ['day', 'week', 'month'] as const
export type TrendGranularity = (typeof TREND_GRANULARITIES)[number]
export const DEFAULT_TREND_METRIC: TrendMetric = 'units'
export const DEFAULT_TREND_GRANULARITY: TrendGranularity = 'day'

const text = z.string().max(200).optional().catch(undefined)

export const dataVaultSearchSchema = primaryFilterSearchSchema().extend({
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the grid, from 1. Default 1.'),
  limit: z
    .union([z.literal(10), z.literal(25), z.literal(50), z.literal(75), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page. Default 50.'),
  sort: z
    .string()
    .max(60)
    .optional()
    .catch(undefined)
    .describe('The API field the rows are sorted by; absent is the dataset’s own order.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction for `sort`. Default ascending.'),
  level: z
    .enum(VIEW_MODES)
    .optional()
    .catch(undefined)
    .describe('Raw rows, or a roll-up by SKU, brand, category or subcategory. Default rows.'),
  q: text.describe('Free-text search over the page’s rows.'),
  /** SKU Management's category filter. */
  category: text.describe('Category to narrow to.'),
  /** Orders' fulfilment filter. */
  fulfilment: z
    .enum(['fba', 'fbm'])
    .optional()
    .catch(undefined)
    .describe('Fulfilled by the marketplace (fba) or by the seller (fbm); absent is both.'),
  /** `column:op:value`, as the API takes it. */
  filters: z
    .array(z.string().max(120))
    .catch([])
    .describe(
      'Column filters as `column:op:value`, op one of gt, gte, lt, lte, eq, ne, contains, in, nin.',
    ),
  /** The visible columns, once the person has changed them; unset means the dataset's defaults. */
  columns: z
    .array(z.string().max(60))
    .optional()
    .catch(undefined)
    .describe('The visible columns, by id; absent is the dataset’s defaults.'),
  /**
   * The person pressed Load data / Get details. Pages whose filters are all
   * optional fetch nothing until then, so opening one costs no query; set in
   * the URL, a shared link still shows what its sender loaded.
   */
  loaded: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe(
      'True once Load data / Get details was pressed; pages with a load gate fetch nothing until it is set.',
    ),
  /** The record open in a detail panel (Orders: the order's id), so the view is linkable. */
  detail: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The record open in a detail panel (Orders: the order’s id).'),
  /** View, or Edit with its drafts (which never go in the URL). */
  mode: z
    .enum(['view', 'edit'])
    .optional()
    .catch(undefined)
    .describe('View, or Edit with changes submitted for approval. Default view.'),
  metric: z
    .enum(TREND_METRICS)
    .optional()
    .catch(undefined)
    .describe('The trend chart’s figure. Default units.'),
  granularity: z
    .enum(TREND_GRANULARITIES)
    .optional()
    .catch(undefined)
    .describe('The trend chart’s period. Default day.'),
  /** The page's tab, where it has them (Costs, Internal SKUs). */
  tab: z
    .string()
    .max(40)
    .optional()
    .catch(undefined)
    .describe('The page’s tab, where it has them (Internal SKUs: `costs`).'),
  costType: z
    .enum(['vendor', 'retailer'])
    .optional()
    .catch(undefined)
    .describe('Costs: vendor costs or retailer costs. Default vendor.'),
  /** Costs' party and currency, as VentaHub's cost filters. */
  vendorIds: z
    .array(z.number().int().positive())
    .optional()
    .catch(undefined)
    .describe('Vendors to narrow costs to, by id.'),
  retailerIds: z
    .array(z.number().int().positive())
    .optional()
    .catch(undefined)
    .describe('Retailers to narrow costs to, by id.'),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe('Currency code (3 letters) to narrow costs to.'),
})

export type DataVaultSearch = z.infer<typeof dataVaultSearchSchema>

export const EMPTY_SEARCH: DataVaultSearch = dataVaultSearchSchema.parse({})

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutDataVaultDefaults(search: DataVaultSearch): DataVaultSearch {
  return pruned({
    ...search,
    limit: search.limit === DEFAULT_PAGE_SIZE ? undefined : search.limit,
    level: search.level === DEFAULT_VIEW ? undefined : search.level,
    mode: search.mode === 'view' ? undefined : search.mode,
    costType: search.costType === 'vendor' ? undefined : search.costType,
    metric: search.metric === DEFAULT_TREND_METRIC ? undefined : search.metric,
    granularity: search.granularity === DEFAULT_TREND_GRANULARITY ? undefined : search.granularity,
  })
}

/** The level tab, with `rows` as the default; the roll-up levels are the API's. */
export function viewMode(search: Pick<DataVaultSearch, 'level'>): ViewMode {
  return search.level ?? DEFAULT_VIEW
}

export function isAggregateLevel(mode: ViewMode): mode is ViewLevel {
  return mode !== 'rows'
}

/**
 * The URL's `column:op:value` entries as the table's column filters; see
 * `fromFilterParams`.
 */
export function parseColumnFilters(filters: readonly string[]): ColumnFiltersState {
  return fromFilterParams(filters)
}

/** The inverse of `parseColumnFilters`: what goes into the URL. */
export function serializeColumnFilters(state: ColumnFiltersState): string[] {
  return toFilterParams(state)
}

/** The API's filter parameters for the current URL, shared by list, aggregate and export. */
export function toFilterQuery(search: DataVaultSearch): DataVaultFilterParams {
  const query = search.q?.trim()
  return {
    ...toApiParams(search),
    vendor_ids: search.vendorIds?.length ? search.vendorIds : undefined,
    retailer_ids: search.retailerIds?.length ? search.retailerIds : undefined,
    currency_codes: search.currency ? [search.currency] : undefined,
    search: query ? query : undefined,
    filter: search.filters.length ? search.filters : undefined,
    sort: search.sort,
    order: search.sort ? (search.order ?? 'asc') : undefined,
  }
}

/** The list call's parameters: the filters plus the page and its size. */
export function toListQuery(search: DataVaultSearch): DataVaultListParams {
  return {
    ...toFilterQuery(search),
    page: search.page ?? 1,
    limit: search.limit ?? DEFAULT_PAGE_SIZE,
  }
}

/** Anything narrowing the rows — the reason an empty table may be empty. */
/** The columns on screen: the person's choice, else the dataset's defaults. */
export function visibleColumns(
  search: Pick<DataVaultSearch, 'columns'>,
  defaults: readonly string[],
): readonly string[] {
  return search.columns ?? defaults
}

export function hasAnyFilter(search: DataVaultSearch): boolean {
  return Boolean(
    search.warehouseIds.length ||
    search.marketplaceIds.length ||
    search.storeIds.length ||
    search.brandIds.length ||
    search.dateFrom ||
    search.dateTo ||
    search.sku ||
    search.asin ||
    search.q ||
    search.vendorIds?.length ||
    search.retailerIds?.length ||
    search.currency ||
    search.filters.length,
  )
}
